import { useState, useEffect, useMemo, useCallback, useReducer } from 'preact/hooks';
import type { WorkItemGroup, WorkItemSummary } from '@voice-assistant/shared';
import {
  buildGrafanaCiUrl,
  GRAFANA_SIGNIN_URL,
} from '../utils/grafanaCiUrl';
import { workItemsLoadReducer, WORK_ITEMS_LOAD_INITIAL } from '../state/workItemsLoad';
import { useUiState, useUiStateStore, setWorkItemsRepos } from '../state/uiStateStore';

/* ── Props ─────────────────────────────────────────────────────────── */

export interface GrafanaTasksPanelProps {
  /** When true the panel is visible/active → (re)fetch the work-item picker list. */
  active: boolean;
  /** "▶ Work on this" action — prefills the composer (useChatShell.handleWorkOnItem). */
  onWorkOnItem?: (wi: WorkItemSummary) => void;
}

/* ── Component ─────────────────────────────────────────────────────── */

/**
 * The "✅ Tasks" drawer in Grafana mode (ci-observability-grafana-dashboard.md §5.2).
 *
 * Replaces WorkItemsPanel's read-only list rendering with a kiosk iframe of the CI
 * Factory Floor dashboard — the observational 80% (issues, PRs, CI runs, timing,
 * concurrency, alarms) is now Grafana's job. A THIN NATIVE STRIP above the frame keeps
 * the mutating 20% an iframe cannot do:
 *   (a) repo filter chips that REWRITE the iframe's `var-repo` params (the chips are the
 *       dashboard's controller — this sidesteps the Grafana 11.5 kiosk variable-dropdown
 *       leak by driving the variable from outside the frame), and
 *   (b) a "▶ Work on this" picker fed by the existing /api/work-items read endpoints that
 *       prefills the agent-chat composer via handleWorkOnItem.
 *
 * Same `selectedRepos` Set drives BOTH the picker's fetch and the iframe's var-repo —
 * one source of truth for "which repos". Since ui-state Phase 2 (#1979) that truth is
 * `uiStateStore.workItemsRepos` (the old ad-hoc localStorage key it shared with
 * WorkItemsPanel was folded into the store's one-time legacy migration, R-A4) — and
 * because both panels now subscribe to the SAME store key, repo changes sync between
 * the two panels live (the ad-hoc key never could).
 *
 * The "Sign in to Grafana" fallback link is ALWAYS present (§5.3): voice.akrsmv.net and
 * grafana.akrsmv.net are same-site but cross-ORIGIN, so an XFO/no-session block cannot be
 * detected from here (no contentDocument access; the frame's `error` event does not fire
 * for an X-Frame-Options refusal). Presence, not error-detection, is what guarantees the
 * user is never stranded on a blank frame.
 */
export function GrafanaTasksPanel({ active, onWorkOnItem }: GrafanaTasksPanelProps) {
  // ── Repo selection — SHARED with WorkItemsPanel via uiStateStore (owner/repo slugs) ──
  const [availableRepos, setAvailableRepos] = useState<string[]>([]);
  const workItemsRepos = useUiState((s) => s.workItemsRepos);
  const selectedRepos = useMemo(() => new Set(workItemsRepos), [workItemsRepos]);

  // ── Work-item picker state ──
  const [items, setItems] = useState<Array<WorkItemSummary & { repoSlug: string }>>([]);
  const [pickedKey, setPickedKey] = useState<string>('');
  // R-18 (D-VA-1): the picker's load phase is the workItemsLoad FSM — a failed fetch
  // REFUSES ("could not be loaded" + Retry), it never reads as "No open work items".
  const [load, dispatchLoad] = useReducer(workItemsLoadReducer, WORK_ITEMS_LOAD_INITIAL);
  const itemsLoading = load.phase === 'loading';

  // Fetch available repos once (same endpoint as WorkItemsPanel).
  useEffect(() => {
    (async () => {
      try {
        const { authFetch } = await import('../api/authFetch');
        const res = await authFetch('/api/work-items/repos');
        if (res.ok) {
          const repos: string[] = await res.json();
          setAvailableRepos(repos);
          // On first load, if no selection saved, select all repos
          if (useUiStateStore.getState().workItemsRepos.length === 0) {
            setWorkItemsRepos(repos);
          }
        }
      } catch { /* ignore */ }
    })();
  }, []);

  const toggleRepo = useCallback((repo: string) => {
    const next = new Set(useUiStateStore.getState().workItemsRepos);
    if (next.has(repo)) next.delete(repo);
    else next.add(repo);
    setWorkItemsRepos([...next]);
  }, []);

  // Fetch the flat open-work-item list for the picker (read-only; the iframe shows the
  // rich view). Scoped to the selected repos so the picker matches what the iframe shows.
  const fetchItems = useCallback(async () => {
    dispatchLoad({ type: 'FETCH' });
    try {
      const { authFetch } = await import('../api/authFetch');
      const repos = [...selectedRepos].filter(Boolean);
      const params = new URLSearchParams();
      if (repos.length) params.set('repos', repos.join(','));
      const qs = params.toString();
      const res = await authFetch(`/api/work-items${qs ? '?' + qs : ''}`);
      if (!res.ok) {
        setItems([]);
        dispatchLoad({ type: 'FAIL', error: `the server answered ${res.status}` });
        return;
      }
      const groups: WorkItemGroup[] = await res.json();
      const flat = groups.flatMap((g) =>
        g.items
          .filter((wi) => (wi.kind === 'pr' ? wi.prState === 'open' : wi.state?.toLowerCase() === 'open'))
          .map((wi) => ({ ...wi, repoSlug: wi.repoSlug ?? g.repoSlug })),
      );
      setItems(flat);
      dispatchLoad({ type: 'OK' });
    } catch (err) {
      setItems([]);
      dispatchLoad({ type: 'FAIL', error: err instanceof Error && err.message ? err.message : 'the request failed' });
    }
  }, [selectedRepos]);

  useEffect(() => {
    if (active) fetchItems();
  }, [active, fetchItems]);

  // ── Iframe src — reactively rebuilt from the SAME selection the chips drive ──
  const grafanaSrc = useMemo(
    () => buildGrafanaCiUrl({ selectedRepos: [...selectedRepos], availableRepos }),
    [selectedRepos, availableRepos],
  );

  const pickedItem = useMemo(
    () => items.find((wi) => `${wi.repo}-${wi.kind}-${wi.number}` === pickedKey),
    [items, pickedKey],
  );

  const handleWorkOn = useCallback(() => {
    if (pickedItem && onWorkOnItem) onWorkOnItem(pickedItem);
  }, [pickedItem, onWorkOnItem]);

  return (
    <div class="gtp-root">
      {/* ── Thin native strip: the mutating 20% ── */}
      <div class="gtp-strip">
        <div class="gtp-workon">
          <select
            class="gtp-workon-select"
            value={pickedKey}
            aria-label="Select a work item"
            onChange={(e) => setPickedKey((e.target as HTMLSelectElement).value)}
          >
            <option value="">
              {itemsLoading ? 'Loading work items…' : load.phase === 'error' ? 'Work items could not be loaded' : items.length ? 'Select a work item…' : 'No open work items'}
            </option>
            {items.map((wi) => (
              <option key={`${wi.repo}-${wi.kind}-${wi.number}`} value={`${wi.repo}-${wi.kind}-${wi.number}`}>
                {wi.kind === 'pr' ? 'PR' : 'issue'} #{wi.number} · {wi.repoSlug} — {wi.title}
              </option>
            ))}
          </select>
          <button
            type="button"
            class="gtp-workon-btn"
            disabled={!pickedItem}
            onClick={handleWorkOn}
          >
            ▶ Work on this
          </button>
          {load.phase === 'error' && (
            <span class="gtp-refusal" role="alert">
              Work items could not be loaded — {load.error}.{' '}
              <button type="button" class="gtp-workon-btn" onClick={() => fetchItems()}>Retry</button>
            </span>
          )}
        </div>

        {availableRepos.length > 0 && (
          <div class="gtp-chips" role="group" aria-label="Filter dashboard by repo">
            {availableRepos.map((repo) => {
              const selected = selectedRepos.has(repo);
              const slug = repo.includes('/') ? repo.split('/').pop()! : repo;
              return (
                <button
                  key={repo}
                  type="button"
                  class={`gtp-chip${selected ? ' active' : ''}`}
                  aria-pressed={selected}
                  onClick={() => toggleRepo(repo)}
                >
                  {slug}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Grafana render surface ── */}
      <iframe
        class="gtp-iframe"
        src={grafanaSrc}
        title="CI Factory Floor"
        // Same-site cookie ride; sandbox kept permissive enough for Grafana's own scripts.
        allow="fullscreen"
      />

      {/* ── Always-present no-session fallback (§5.3): can't detect a cross-origin XFO
             block from here, so the escape hatch is unconditional, never gated on an
             error event that won't fire. ── */}
      <div class="gtp-fallback">
        Frame blank or signed out?{' '}
        <a class="gtp-fallback-link" href={GRAFANA_SIGNIN_URL} target="_blank" rel="noopener noreferrer">
          Sign in to Grafana ↗
        </a>
      </div>
    </div>
  );
}
